import * as angular from 'angular';
import { distinctUntilChanged, filter, first, map, skip, take, takeUntil } from 'rxjs/operators';

import { AppStore } from '~/types/main.types';
import { NotebookType, NotebookSidebarType } from '@mode/notebooks/contract';
import { FeatureFlag, FeatureFlagsFacade, OrgTypes } from '@mode/shared/contract-common';

import { ClientActionType } from '../../../actions/client.actions';
import { MixableBaseController } from '../../../common/component';
import { PreferenceResource } from '@mode/legacy/data-access-model-js';
import { accountContextPlanCode, accountPreferenceSelector } from '../../../reducers/selectors';
import { NotebookSelectors } from '../../../selectors/notebook.selectors';
import { AnalyticsService, AnalyticsEvent, RunMethodType } from '@mode/shared/util-segment';
import { NotebookDataService } from '../../../services/notebook-data-service';
import keyboard_shortcuts from './notebook-sidebar-content/keyboard_shortcuts.html';
import python_intro from './notebook-sidebar-content/python_intro.html';
import r_intro from './notebook-sidebar-content/r_intro.html';
import { Observable } from 'rxjs';
import { getInitialFlagValue, HelpDocsService } from '@mode/shared/util-js';

class NotebookSidebarController extends MixableBaseController {
  static $inject = [
    '$element',
    'RxStoreService',
    'NotebookDataService',
    'AnalyticsService',
    'FeatureFlagsFacade',
    'HelpDocsService',
  ];

  notebookToken: string;
  notebookType: NotebookType;
  isPythonEdge: boolean;
  activeTab: NotebookSidebarType;
  itemExpandedOrCollapsed: { [key: string]: boolean };
  isPythonEdgeNotebooksEnabled: boolean;
  isRegisterImportedDatasetEnabled$: Observable<boolean>;
  isNotebookLibraryCleanslateEnabled$: Observable<boolean>;
  isRegisterSecureCredentialsEnabled: boolean;
  notebookSidebarMigration = getInitialFlagValue(FeatureFlag.NotebookSidebarMigration);

  private LANGUAGES = [
    { type: NotebookType.Python3, name: 'Python 3' },
    { type: NotebookType.R, name: 'R' },
  ];

  constructor(
    private $element: angular.IAugmentedJQuery,
    private store: AppStore,
    private notebookDataService: NotebookDataService,
    private analytics: AnalyticsService,
    private featureFlagService: FeatureFlagsFacade,
    public helpDocs: HelpDocsService
  ) {
    super();
  }

  $onInit() {
    this.store
      .select(NotebookSelectors.selectedNotebookType)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((type) => {
        this.notebookType = type;
        this.changeIsPythonEdge();
      });
    this.store
      .select(NotebookSelectors.selectedNotebookToken)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((token) => (this.notebookToken = token));

    this.store
      .select(accountPreferenceSelector)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ notebook_sidebar: { tab, items } }) => {
        this.activeTab = tab;
        this.itemExpandedOrCollapsed = items;
      });

    this.store
      .select(accountPreferenceSelector)
      .pipe(first<Readonly<PreferenceResource>>(Boolean))
      .subscribe(({ notebook_sidebar: { tab } }) => {
        this.track(AnalyticsEvent.Name.NotebookSidebarContentImpression, 'impression', { tab });
      });

    this.store
      .select(accountPreferenceSelector)
      .pipe(
        filter<Readonly<PreferenceResource>>(Boolean),
        map(({ notebook_sidebar: { tab } }) => tab),
        skip(1),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((tab) => {
        this.track(AnalyticsEvent.Name.NotebookSidebarTabChange, 'click', { tab });
      });

    this.store
      .selectNoEmpty(accountContextPlanCode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((billingPlanCode) => {
        if (billingPlanCode === OrgTypes.PlanCode.Plus) {
          this.LANGUAGES.push({ type: NotebookType.Python3Edge, name: 'Python 3 Edge' });
        }
      });

    this.isRegisterImportedDatasetEnabled$ = this.featureFlagService
      .asObservable(FeatureFlag.RegisterImportedDatasetsNotebooks)
      .pipe(takeUntil(this.destroyed$));
    this.featureFlagService
      .asObservable(FeatureFlag.RegisterSecureCredentialsNotebooks)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((flag) => {
        this.isRegisterSecureCredentialsEnabled = flag;
        if (!flag && this.activeTab === NotebookSidebarType.Secrets) {
          this.goToTab(NotebookSidebarType.Resources);
        }
      });

    this.featureFlagService
      .asObservable(FeatureFlag.NotebookLibraryCleanslate)
      .pipe(take(1))
      .subscribe((flag) => {
        flag && this.LANGUAGES.push({ type: NotebookType.Python3CleanSlate, name: 'Python 3 Cleanslate' });
      });
  }

  track(event: AnalyticsEvent.Name, interaction_type: 'click' | 'impression', props = {}) {
    this.analytics.run(RunMethodType.Track, event, {
      product_context: AnalyticsEvent.Context.Notebook,
      product_subcontext: AnalyticsEvent.SubContext.Sidebar,
      interaction_type,
      notebook_token: this.notebookToken,
      notebook_kernel: this.notebookType,
      ...props,
    });
  }

  changeNotebookType() {
    this.changeIsPythonEdge();
    if (this.notebookType && this.notebookToken) {
      this.notebookDataService.updateNotebookType(this.notebookToken, this.notebookType);
      this.analytics.run(RunMethodType.Track, AnalyticsEvent.Name.NotebookEnvironmentSelectorClicked);
    }
    if (this.notebookType === NotebookType.R && this.activeTab === NotebookSidebarType.Secrets) {
      this.goToTab(NotebookSidebarType.Resources);
    }
  }
  changeIsPythonEdge() {
    this.isPythonEdge = this.notebookType === NotebookType.Python3Edge;
  }
  goToTab(tab: this['activeTab']) {
    this.update({ tab });
  }
  toggle(item: string) {
    const wasOpen = this.isOpen(item);
    this.update({ items: { [item]: !wasOpen } });
    this.track(AnalyticsEvent.Name.NotebookSidebarExpandableTextToggled, 'click', {
      item,
      was: wasOpen ? 'open' : 'closed',
    });
  }
  isOpen(item: string) {
    return this.itemExpandedOrCollapsed[item];
  }

  goToSupportedLibraries() {
    this.update({ tab: NotebookSidebarType.Resources });
    this.track(AnalyticsEvent.Name.NotebookSidebarResourcesLinkClicked, 'click');
  }
  goToKeyboardShortcuts() {
    this.update({ tab: NotebookSidebarType.Help, items: { intro: false, shortcuts: true } });
    this.$element.find('.notebook-sidebar-sub-container.help').scrollTop(0);
  }

  private update(params: Partial<PreferenceResource['notebook_sidebar']>) {
    params.items = params.items
      ? Object.assign(this.itemExpandedOrCollapsed, params.items)
      : this.itemExpandedOrCollapsed;
    params.tab = params.tab ?? this.activeTab;
    params.open = true;
    this.store.dispatch({
      type: ClientActionType.UpdatePreference,
      params: { notebook_sidebar: params },
    });
  }
}

export const notebookSidebarComponent: angular.IComponentOptions = {
  controller: NotebookSidebarController,
  controllerAs: 'ctrl',
  template: `
    <div class="notebook-sidebar" ng-if="!ctrl.notebookSidebarMigration">
      <div class="notebook-type-select">
        <select
          radio-dropdown="notebook-type"
          id="notebook-type-select"
          block="true"
          append-to-body="true"
          button-classes="notebook-type-dropdown"
          ng-options="lang.type as lang.name for lang in ctrl.LANGUAGES"
          ng-model="ctrl.notebookType"
          ng-change="ctrl.changeNotebookType()"
          dropdown-data="ctrl.notebookType">
        </select>
      </div>

      <div class="notebook-sidebar-container">
        <div class="nav-tabs-table">
          <ul class="nav nav-tabs nav-tabs-bottom nav-tabs-row">
            <li ng-class="{active: ctrl.activeTab === 'resources'}">
              <a href ng-click="ctrl.goToTab('resources')">Resources</a>
            </li>
            <li ng-class="{active: ctrl.activeTab === 'secrets'}" ng-if="ctrl.notebookType !== 'r' && ctrl.isRegisterSecureCredentialsEnabled">
              <a href ng-click="ctrl.goToTab('secrets')">Secrets</a>
            </li>
            <li ng-class="{active: ctrl.activeTab === 'help'}">
              <a href ng-click="ctrl.goToTab('help')">Help</a>
            </li>
          </ul>
        </div>

        <!-- Supported Libraries -->
        <div ng-show="ctrl.activeTab === 'resources'"
          class="notebook-sidebar-sub-container library-container">
          <mode-notebook-supported-libraries
            [environment]="ctrl.notebookType"
            [state]="ctrl.itemExpandedOrCollapsed"
            (section-toggle)="ctrl.toggle($event)">
          </mode-notebook-supported-libraries>
        </div>

        <!-- Notebook Secrets -->
        <div ng-show="ctrl.activeTab === 'secrets'" ng-if="ctrl.notebookType !== 'r' && ctrl.isRegisterSecureCredentialsEnabled"
          class="notebook-sidebar-sub-container library-container">
          <mode-notebook-secrets></mode-notebook-secrets>
        </div>

        <!-- Help -->
        <div ng-show="ctrl.activeTab === 'help'"
          class="notebook-sidebar-sub-container help-container">
          <!-- Python -->
          <ul ng-if="ctrl.notebookType !== 'r'"
            class="notebook-sidebar-collapsible-list">
            ${python_intro}
            ${keyboard_shortcuts}
          </ul>
          <!-- R -->
          <ul ng-if="ctrl.notebookType === 'r'"
            class="notebook-sidebar-collapsible-list">
            ${r_intro}
            ${keyboard_shortcuts}
          </ul>
        </div>
      </div>
    </div>
    <div ng-if="ctrl.notebookSidebarMigration">
      <mode-notebook-sidebar></mode-notebook-sidebar>
    </div>
  `,
};
